import React, { useState, useEffect, useMemo, useCallback, Suspense, lazy } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { NumberBall } from "@/components/NumberBall";
import { SEOHead } from "@/components/SEOHead";
import { Calendar, Target, TrendingUp, Flame, Snowflake, Clock, Award, AlertCircle } from "lucide-react";
import { LotterySubpagesLinks, RelatedLotteriesLinks, BreadcrumbNav } from "@/components/InternalLinksSEO";
import { LotteryKPIDisplay } from "@/components/LotteryKPIDisplay";

import { API } from "@/config/api";

// Lazy load heavy components
const WinningOdds = lazy(() => import("@/components/WinningOdds"));

// Skeleton component for loading states
const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse bg-gray-700/50 rounded ${className}`} />
);

const MegaMillionsPage = () => {
  const { lang = 'en' } = useParams();

  // Split state by priority - critical data loads first
  const [latestDraw, setLatestDraw] = useState(null);
  const [nextDraw, setNextDraw] = useState(null);
  const [criticalLoading, setCriticalLoading] = useState(true);
  
  // Secondary data loads after initial render
  const [predictions, setPredictions] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [recentDraws, setRecentDraws] = useState([]);
  const [kpiV2Data, setKpiV2Data] = useState(null);
  const [secondaryLoading, setSecondaryLoading] = useState(true);

  // Guards to prevent duplicate fetches
  const criticalFetchedRef = React.useRef(false);

  // Memoized date formatter
  const safeFormatDate = useCallback((rawDate) => {
    if (!rawDate) return "N/A";
    const d = new Date(rawDate);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }, []);

  // Helper to get Mega Ball from various field names
  const getMegaBall = useCallback((draw) => {
    return draw?.mega_ball || draw?.mega_ball_number || draw?.bonus_number || draw?.bonus_numbers?.[0] || draw?.extra_numbers?.[0] || null;
  }, []);

  // STEP 1: Load critical data ONLY (latest + next draw) - fast
  useEffect(() => {
    if (criticalFetchedRef.current) return;
    criticalFetchedRef.current = true;
    const loadCritical = async () => {
      try {
        const [latestRes, nextRes] = await Promise.all([
          axios.get(`${API}/lottery/megamillions/latest`),
          axios.get(`${API}/lottery/megamillions/next`),
        ]);
        setLatestDraw(latestRes.data);
        setNextDraw(nextRes.data);
      } catch (e) {
        console.error("MegaMillions critical load error:", e);
      } finally {
        setCriticalLoading(false);
      }
    };
    loadCritical();
  }, []);

  // STEP 2: Load secondary data AFTER first render (deferred)
  useEffect(() => {
    if (criticalLoading) return;
    let cancelled = false;

    const loadSecondary = async () => {
        try {
          const results = await Promise.allSettled([
            axios.get(`${API}/lottery/megamillions/predictions`),
            axios.get(`${API}/lotteries/megamillions/stats?window=50`),
            axios.get(`${API}/lotteries/megamillions/draws?limit=10`),
            axios.get(`${API}/kpi-v2/megamillions`),
          ]);

          const [preds, stats, draws, kpiV2] = results.map((r) =>
            r.status === "fulfilled" ? r.value.data : null
          );

          setPredictions(preds);
          setStatistics(stats);
          setRecentDraws(draws?.draws || []);
          setKpiV2Data(kpiV2);
        } catch (e) {
          console.error("MegaMillions secondary load error:", e);
        } finally {
          if (!cancelled) setSecondaryLoading(false);
        }
      };
      loadSecondary();

    return () => { cancelled = true; };
  }, [criticalLoading]);

  // Memoize derived data
  const hotNumbers = useMemo(() => statistics?.hot_numbers || [], [statistics]);
  const coldNumbers = useMemo(() => statistics?.cold_numbers || [], [statistics]);
  const overdueNumbers = useMemo(() => statistics?.overdue_numbers || [], [statistics]);

  const formatPI = useCallback((pi) => {
    if (pi === null || pi === undefined) return 'N/A';
    return `${pi.toFixed(2)}×`;
  }, []);

  // RENDER IMMEDIATELY - No blocking loading screen
  return (
    <div className="min-h-screen py-4">
      <SEOHead 
        title="Mega Millions Results, Statistics & Predictions"
        description="Mega Millions results, statistics, and algorithmic predictions. Draws Tuesday & Friday. 5 numbers from 1-70 plus 1 Mega Ball from 1-25."
        path="/lottery/megamillions"
      />

      <div className="max-w-7xl mx-auto px-4">
        {/* BREADCRUMB NAV */}
        <BreadcrumbNav 
          items={[{ label: 'Mega Millions' }]} 
          className="mb-3"
        />
        
        {/* HEADER - Always renders instantly */}
        <div className="mb-4">
          <h1 className="text-3xl font-bold text-white mb-1">Mega Millions 🇺🇸</h1>
          <p className="text-gray-400 text-sm">
            Draws: Tuesday & Friday | 5 numbers (1-70) + 1 Mega Ball (1-25)
          </p>

          <div className="flex gap-2 mt-3">
            <Link to={`/${lang}/predictions?lottery=megamillions`}>
              <Button size="sm" className="bg-amber-500 hover:bg-amber-600">
                <Target className="w-3 h-3 mr-1" />
                Generate Prediction
              </Button>
            </Link>

            <Link to={`/${lang}/statistics?lottery=megamillions`}>
              <Button
                size="sm"
                variant="outline"
                className="border-gray-700 text-white hover:bg-gray-800"
              >
                <TrendingUp className="w-3 h-3 mr-1" />
                View Statistics
              </Button>
            </Link>
          </div>
          
          <LotterySubpagesLinks 
            lotteryCode="megamillions" 
            lotteryName="Mega Millions"
            className="mt-4"
          />
        </div>

        {/* DISCLAIMER BOX */}
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-gray-300 text-xs leading-relaxed">
              <strong className="text-blue-400">About These Predictions:</strong> All predictions 
              are generated by statistical algorithms analyzing historical data. They are 
              provided for educational purposes only. Lottery draws are random events - 
              no method can improve your odds of winning.
            </p>
          </div>
        </div>

        {/* MAIN LAYOUT */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3.5 mb-3">
          
          {/* LEFT SECTION */}
          <div className="lg:col-span-3 space-y-3">
            
            {/* TOP ROW: Latest Draw / Next Draw / Predictions */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
              
              {/* LATEST DRAW - Critical */}
              <Card className="bg-gray-900/40 border-gray-700/50">
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-white text-sm">Latest Draw</CardTitle>
                  <span className="text-[10px] text-gray-400 block">
                    {criticalLoading ? <Skeleton className="w-16 h-3 inline-block" /> : safeFormatDate(latestDraw?.date)}
                  </span>
                </CardHeader>

                <CardContent className="pt-0 pb-2 px-3">
                  {criticalLoading ? (
                    <div className="flex flex-wrap gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} className="w-6 h-6 rounded-full" />
                      ))}
                    </div>
                  ) : latestDraw?.main_numbers?.length ? (
                    <>
                      <div className="flex flex-wrap gap-1 mb-1">
                        {latestDraw.main_numbers.map((n, i) => (
                          <NumberBall key={i} number={n} variant="main" size="xs" />
                        ))}
                      </div>
                      {getMegaBall(latestDraw) && (
                        <div className="text-[10px] text-gray-400 flex gap-1 items-center">
                          MB:
                          <NumberBall number={getMegaBall(latestDraw)} variant="bonus" size="xs" />
                        </div>
                      )}
                      {/* Prior Draw Jackpot */}
                      {(latestDraw?.latest_draw_jackpot || latestDraw?.jackpot) && (
                        <div className="mt-1 pt-1 border-t border-gray-700/50">
                          <p className="text-[9px] text-gray-500">
                            Prior Jackpot: <span className="text-amber-400">${(latestDraw.latest_draw_jackpot || latestDraw.jackpot).toLocaleString()}</span>
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-400 text-xs">No recent draws</p>
                  )}
                </CardContent>
              </Card>

              {/* NEXT DRAW - Critical */}
              <Card className="bg-gray-900/40 border-gray-700/50">
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-white text-sm flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Next Draw
                  </CardTitle>
                </CardHeader>

                <CardContent className="pt-0 pb-2 px-3 text-white">
                  {criticalLoading ? (
                    <Skeleton className="w-24 h-5" />
                  ) : (
                    <>
                      <p className="text-base font-bold">
                        {safeFormatDate(nextDraw?.next_draw_date)}
                      </p>
                      {(nextDraw?.next_jackpot_estimate || nextDraw?.next_jackpot) && (
                        <p className="text-green-400 text-sm font-semibold">
                          Est: ${(nextDraw.next_jackpot_estimate || nextDraw.next_jackpot).toLocaleString()}
                        </p>
                      )}
                      {/* Show prior draw context if available */}
                      {nextDraw?.latest_draw_jackpot && nextDraw.latest_draw_jackpot > (nextDraw.next_jackpot_estimate || 0) && (
                        <p className="text-[9px] text-gray-500 mt-1">
                          Prior: ${nextDraw.latest_draw_jackpot.toLocaleString()}
                        </p>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              {/* PREDICTIONS - Secondary */}
              <Card className="bg-gray-900/40 border-gray-700/50">
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-white text-sm">Predictions</CardTitle>
                </CardHeader>

                <CardContent className="pt-0 pb-2 px-3">
                  {secondaryLoading ? (
                    <div className="space-y-2">
                      <Skeleton className="w-full h-4" />
                      <Skeleton className="w-3/4 h-4" />
                    </div>
                  ) : predictions?.current_predictions?.length > 0 ? (
                    <div className="space-y-1.5">
                      <div>
                        <h4 className="text-[9px] font-semibold text-blue-400 mb-0.5">📊 Statistical</h4>
                        {(() => {
                          const p = predictions.current_predictions.find((x) => x.model === "statistical");
                          if (!p) return null;
                          return (
                            <div className="flex flex-wrap gap-0.5">
                              {p.main_numbers?.slice(0, 5).map((n, i) => (
                                <NumberBall key={i} number={n} variant="main" size="xs" />
                              ))}
                              {getMegaBall(p) && (
                                <>
                                  <span className="text-gray-500 mx-0.5">+</span>
                                  <NumberBall number={getMegaBall(p)} variant="bonus" size="xs" />
                                </>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                      <div>
                        <h4 className="text-[9px] font-semibold text-amber-400 mb-0.5">🔥 Lotterics</h4>
                        {(() => {
                          const p = predictions.current_predictions.find((x) => x.model === "lotteryx");
                          if (!p) return null;
                          return (
                            <div className="flex flex-wrap gap-0.5">
                              {p.main_numbers?.slice(0, 5).map((n, i) => (
                                <NumberBall key={i} number={n} variant="main" size="xs" />
                              ))}
                              {getMegaBall(p) && (
                                <>
                                  <span className="text-gray-500 mx-0.5">+</span>
                                  <NumberBall number={getMegaBall(p)} variant="bonus" size="xs" />
                                </>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-400 text-xs">No predictions available</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* SECOND ROW: Hot / Cold / Overdue */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
              
              <Card className="bg-gray-900/40 border-gray-700/50">
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-white text-sm flex items-center gap-2">
                    <Flame className="w-4 h-4 text-red-400" />
                    Hot Numbers
                  </CardTitle>
                  <p className="text-[9px] text-gray-500 mt-0.5">Most frequent in last 50 draws</p>
                </CardHeader>
                <CardContent className="pt-0 pb-2 px-3">
                  {secondaryLoading ? (
                    <div className="flex flex-wrap gap-1">
                      {[...Array(6)].map((_, i) => (
                        <Skeleton key={i} className="w-6 h-6 rounded-full" />
                      ))}
                    </div>
                  ) : hotNumbers.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {hotNumbers.slice(0, 6).map((n, i) => (
                        <NumberBall key={i} number={n} variant="hot" size="xs" />
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">No data</p>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-gray-900/40 border-gray-700/50">
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-white text-sm flex items-center gap-2">
                    <Snowflake className="w-4 h-4 text-blue-400" />
                    Cold Numbers
                  </CardTitle>
                  <p className="text-[9px] text-gray-500 mt-0.5">Least frequent in last 50 draws</p>
                </CardHeader>
                <CardContent className="pt-0 pb-2 px-3">
                  {secondaryLoading ? (
                    <div className="flex flex-wrap gap-1">
                      {[...Array(6)].map((_, i) => (
                        <Skeleton key={i} className="w-6 h-6 rounded-full" />
                      ))}
                    </div>
                  ) : coldNumbers.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {coldNumbers.slice(0, 6).map((n, i) => (
                        <NumberBall key={i} number={n} variant="cold" size="xs" />
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">No data</p>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-gray-900/40 border-gray-700/50">
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-white text-sm flex items-center gap-2">
                    <Clock className="w-4 h-4 text-purple-400" />
                    Overdue Numbers
                  </CardTitle>
                  <p className="text-[9px] text-gray-500 mt-0.5">Longest since last appearance</p>
                </CardHeader>
                <CardContent className="pt-0 pb-2 px-3">
                  {secondaryLoading ? (
                    <div className="flex flex-wrap gap-1">
                      {[...Array(6)].map((_, i) => (
                        <Skeleton key={i} className="w-6 h-6 rounded-full" />
                      ))}
                    </div>
                  ) : overdueNumbers.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {overdueNumbers.slice(0, 6).map((item, i) => (
                        <NumberBall key={i} number={typeof item === 'object' ? item.number : item} variant="main" size="xs" />
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">No data</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* RECENT DRAWS */}
            <Card className="bg-gray-900/40 border-gray-700/50">
              <CardHeader className="py-2 px-3">
                <CardTitle className="text-white text-sm">Recent 10 Draws</CardTitle>
              </CardHeader>

              <CardContent className="pt-0 pb-2 px-3 overflow-x-auto">
                {secondaryLoading ? (
                  <div className="space-y-2">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="w-full h-6" />
                    ))}
                  </div>
                ) : recentDraws.length ? (
                  <table className="w-full text-xs">
                    <tbody>
                      {recentDraws.map((d, idx) => (
                        <tr key={d.id || idx} className="border-b border-gray-700/30 last:border-0">
                          <td className="py-1.5 text-gray-300 w-24">
                            {safeFormatDate(d.draw_date || d.date)}
                          </td>
                          <td className="py-1.5">
                            <div className="flex flex-wrap gap-1 items-center">
                              {d.main_numbers.map((n, i) => (
                                <NumberBall key={i} number={n} variant="main" size="xs" />
                              ))}
                              {getMegaBall(d) && (
                                <>
                                  <span className="text-gray-500 mx-1">+</span>
                                  <NumberBall number={getMegaBall(d)} variant="bonus" size="xs" />
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-xs text-gray-400">No recent draws</p>
                )}
              </CardContent>
            </Card>
          </div>

                    {/* RIGHT SECTION */}
          <div className="space-y-3">
            {/* KPI v2 Display */}
            <LotteryKPIDisplay 
              kpiData={kpiV2Data} 
              loading={secondaryLoading}
              className="bg-gray-900/40 border-gray-700/50"
            />

            {/* ADVERTISEMENT */}{/* ADVERTISEMENT */}
            <div className="bg-gray-900/40 border border-gray-700/50 rounded-lg p-3">
              <div className="w-full h-24 bg-gradient-to-b from-amber-500/10 to-amber-600/5 rounded-lg flex items-center justify-center border border-amber-500/20">
                <div className="text-center">
                  <div className="text-amber-500/60 text-xs font-medium mb-0.5">
                    Advertisement
                  </div>
                  <div className="text-amber-500/40 text-[10px]">300 × 100</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* WINNING ODDS - Lazy loaded */}
        <Suspense fallback={<Skeleton className="w-full h-32 mt-4" />}>
          <WinningOdds lotteryCode="megamillions" />
        </Suspense>
        
        {/* RELATED LOTTERIES */}
        <div className="mt-8 pt-6 border-t border-gray-800">
          <RelatedLotteriesLinks 
            currentLotteryCode="megamillions"
            title="Explore Other Lotteries"
          />
        </div>
      </div>
    </div>
  );
};

export default MegaMillionsPage;
